// ============================================================================
// Feuille du noyau : AFFIRMATIONS DE LIMITES (refonte, lot 6.62, moteur v57 —
// docs/moteur/REFONTE_MOTEUR.md §5.82).
// ============================================================================
// Un QCM « Quelles limites valent +∞ ? », « Quelles suites tendent vers 0 ? » : chaque
// choix est une limite affirmée (`kernel.lclaims`, dans l'ordre des choix) — une
// expression, sa variable, son point et la limite affirmée.
//
// L'élève calcule les limites comme sur une feuille de limites (lot 6.58) : chaque ligne
// est jugée par le juge des limites, au point de l'affirmation dont elle parle — une
// ligne « parle » d'une affirmation quand une de ses expressions est l'expression de
// l'affirmation, un de ses morceaux, ou une écriture déjà reliée sur la feuille. Une
// ligne qui ne parle d'aucune affirmation reste sur la feuille, sans HORA. Quand la limite
// d'une expression est établie, son affirmation est tranchée : vraie si c'est la limite
// affirmée, fausse sinon. Avec `single` (réponse unique), l'élimination tranche le reste.
//
// Pur : aucune dépendance à l'app.
// ============================================================================

import type { Chip } from './lexer';
import type { KernelLimitClaim, ScratchModel } from './model';
import type { Line } from './tree';
import {
  createKLimState,
  exprOfText,
  klimCommit,
  klimEstablished,
  klimHolds,
  klimJudge,
  klimLine,
  klimLineAtoms,
  klimPaletteParts,
  klimText,
  type KLimState,
  type KLimVerdict,
} from './kernel-limit';
import { limValText, parsePoint, sameLimit, trueLimit, type LimVal } from './kernel/limit';
import { canon, termKey, type Term } from './kernel/term';

/* ------------------------------ modèle ------------------------------ */

export class LimitClaimsModelError extends Error {}

export interface KLcVerdict extends KLimVerdict {
  /** affirmations que la ligne tranche (indices, avec leur vérité) */
  decided?: { index: number; truth: boolean }[];
  /** l'affirmation dont la ligne parle (indice) */
  claim?: number;
}

interface LcClaim {
  spec: KernelLimitClaim;
  /** l'état de la feuille de limites de cette expression */
  ks: KLimState;
  /** la limite affirmée */
  claimed: LimVal;
  /** la vraie limite (null : le moteur ne sait pas la calculer) */
  real: LimVal | null;
  /** vérité de l'affirmation (null : le moteur ne la tranche pas) */
  truth: boolean | null;
}

export interface KLcState {
  model: ScratchModel;
  claims: LcClaim[];
  decisions: (boolean | null)[];
  single: boolean;
  path: string[];
  /** l'affirmation de chaque ligne du corrigé */
  owner: number[];
  lines: number;
  texts: string[];
}

const ARROW = '→';
const keyOf = (t: Term): string => {
  try {
    return termKey(canon(t));
  } catch {
    return termKey(t);
  }
};

/** une limite affirmée : « +∞ », « −∞ », « 0 », « 3 », « 0⁺ » */
function claimedValue(text: string): LimVal | null {
  const t = text.trim().replace(/^-/, '−');
  if (t === '+∞') return { k: 'inf', s: 1 };
  if (t === '−∞') return { k: 'inf', s: -1 };
  const m = /^(−?\d+(?:,\d+)?)([⁺⁻]?)$/.exec(t);
  if (!m) return null;
  const v = Number(m[1]!.replace('−', '-').replace(',', '.'));
  if (!Number.isFinite(v)) return null;
  const side = m[2] === '⁺' ? 1 : m[2] === '⁻' ? -1 : 0;
  if (side && v !== 0) return null;
  return { k: 'fin', v, side };
}

/** la feuille de limites d'une affirmation (son expression, sa variable, son point) */
function limitModelOf(model: ScratchModel, c: KernelLimitClaim, path: string[]): ScratchModel {
  const target = exprOfText(c.expr);
  if (!target) throw new LimitClaimsModelError(`Expression illisible : « ${c.expr} ».`);
  let P;
  try {
    P = parsePoint(c.point, c.x, c.x === 'n');
  } catch (e) {
    throw new LimitClaimsModelError(e instanceof Error ? e.message : String(e));
  }
  const real = trueLimit(target, P).v;
  // la réponse ne sert qu'à lire la question : la vraie limite si elle s'écrit
  const answer =
    real && (real.k === 'fin' || real.k === 'inf')
      ? `${c.expr} ${ARROW} ${limValText(real)}`
      : `${c.expr} ${ARROW} 0`;
  return {
    ...model,
    symbols: { [c.x]: {} },
    goal: [c.x],
    traps: [],
    choices: undefined,
    givens: undefined,
    kernel: {
      plugin: 'limit',
      goal: { kind: 'limit', target: c.x },
      point: c.point,
      start: c.expr,
      answer,
      path,
    },
  } as unknown as ScratchModel;
}

/* ------------------------------ état ------------------------------ */

export function createKLcState(model: ScratchModel): KLcState {
  const spec = model.kernel!;
  if (spec.plugin !== 'limclaims' || !spec.lclaims)
    throw new LimitClaimsModelError('Affirmations de limites attendues (plugin « limclaims »).');
  const path = spec.path ?? [];
  const build = (paths: string[][]): LcClaim[] =>
    spec.lclaims!.map((c, i) => {
      const claimed = claimedValue(c.value);
      if (!claimed) throw new LimitClaimsModelError(`Limite affirmée illisible : « ${c.value} ».`);
      let ks: KLimState;
      try {
        ks = createKLimState(limitModelOf(model, c, paths[i] ?? []));
      } catch (e) {
        if (e instanceof LimitClaimsModelError) throw e;
        throw new LimitClaimsModelError(e instanceof Error ? e.message : String(e));
      }
      const real = trueLimit(ks.target, ks.P).v;
      const truth = real ? sameLimit(claimed, real) : null;
      return { spec: c, ks, claimed, real, truth };
    });
  const ks: KLcState = {
    model,
    claims: build([]),
    decisions: spec.lclaims.map(() => null),
    single: !!spec.single,
    path,
    owner: [],
    lines: 0,
    texts: [],
  };
  // à quelle affirmation parle chaque ligne du corrigé (coup de pouce)
  const scratch: KLcState = { ...ks, claims: build([]), decisions: [...ks.decisions], texts: [] };
  const owner: number[] = [];
  for (const t of path) {
    const v = klcJudge(scratch, chipsOf(klimLineAtoms(t)), { verify: true });
    owner.push(v.claim ?? -1);
    if (v.kind === 'step' || v.kind === 'useless') klcCommit(scratch, v);
  }
  ks.owner = owner;
  const paths = ks.claims.map((_, i) => path.filter((_, j) => owner[j] === i));
  ks.claims = build(paths);
  return ks;
}

const chipsOf = (atoms: string[]): Chip[] => atoms.map((a) => ({ atoms: [a], text: a }));

/* ------------------------------ lecture d'une ligne ------------------------------ */

const CMP = new Set(['<', '>', '≤', '≥']);

/** les expressions d'une ligne (membres d'une égalité, termes d'un encadrement) */
function lineExprs(atoms: string[]): Term[] {
  const ai = atoms.indexOf(ARROW);
  const left = ai < 0 ? atoms : atoms.slice(0, ai);
  const parts: string[][] = [[]];
  let depth = 0;
  for (const a of left) {
    if (a === '(') depth++;
    if (a === ')') depth--;
    if (depth === 0 && (a === '=' || CMP.has(a))) parts.push([]);
    else parts[parts.length - 1]!.push(a);
  }
  return parts.flatMap((p) => {
    if (!p.length) return [];
    const t = exprOfText(p.join(' '));
    return t ? [t] : [];
  });
}

function subterms(t: Term, out: Set<string>): void {
  out.add(keyOf(t));
  switch (t.k) {
    case 'add':
    case 'mul':
      t.c.forEach((c) => subterms(c, out));
      break;
    case 'fn':
      subterms(t.a, out);
      break;
    case 'div':
    case 'pow':
    case 'binom':
      subterms(t.a, out);
      subterms(t.b, out);
      break;
    default:
  }
}

/** les écritures dont parle l'affirmation : son expression, ses morceaux, les écritures reliées */
function knownKeys(c: LcClaim): Set<string> {
  const out = new Set<string>();
  for (const t of c.ks.terms.values()) subterms(t, out);
  return out;
}

/** une ligne parle-t-elle de cette affirmation ? */
function speaksOf(c: LcClaim, exprs: Term[]): boolean {
  if (!exprs.length) return false;
  const keys = knownKeys(c);
  return exprs.some((e) => keys.has(keyOf(e)));
}

/* ------------------------------ jugement ------------------------------ */

const RANK: Record<KLimVerdict['kind'], number> = {
  step: 10,
  ready: 9,
  useless: 7,
  building: 6,
  pending: 5,
  open: 4,
  unjustified: 3,
  trap: 2,
  false: 1,
  syntax: 0,
  empty: -1,
};

function score(v: KLimVerdict): number {
  let s = RANK[v.kind] * 100 + (v.hora ?? 0);
  if (v.kind === 'useless') s += v.jump ? 20 : v.unknown ? 0 : 10;
  return s;
}

function decisionsOf(ks: KLcState): (boolean | null)[] {
  const d = ks.claims.map((c) => (c.ks.concluded ? c.truth : null));
  if (ks.single) {
    const trues = d.filter((v) => v === true).length;
    if (trues === 1) return d.map((v) => v === true);
    const open = d.filter((v) => v === null).length;
    if (!trues && open === 1) return d.map((v) => v === null);
  }
  return d;
}

export function klcJudge(ks: KLcState, chips: Chip[], opts?: { verify?: boolean }): KLcVerdict {
  const A = chips.flatMap((c) => c.atoms);
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const exprs = lineExprs(A);
  const speaking = ks.claims.map((c, i) => ({ c, i })).filter(({ c }) => speaksOf(c, exprs));
  // la ligne en cours de frappe, ou sans affirmation : on la juge partout (syntaxe, préfixe)
  const pool = speaking.length ? speaking : ks.claims.map((c, i) => ({ c, i }));
  let best: { v: KLimVerdict; i: number } | null = null;
  for (const { c, i } of pool) {
    const v = klimJudge(c.ks, chips, opts);
    if (!best || score(v) > score(best.v)) best = { v, i };
  }
  const v = best!.v;
  // une ligne complète qui ne parle d'aucune affirmation : à côté
  if (!speaking.length && (v.kind === 'step' || v.kind === 'useless' || v.kind === 'ready'))
    return {
      kind: 'useless',
      atoms: A,
      line: klimLine(klimText(A)),
      complete: true,
      title: 'De quelle limite parles-tu ?',
      message:
        'Ta ligne ne parle d’aucune des expressions de l’énoncé : pars de l’une d’elles (ou d’un de ses morceaux).',
    };
  const out: KLcVerdict = { ...v, claim: best!.i };
  // des points différents : une ligne fausse dit à quel point elle est jugée
  const c0 = ks.claims[best!.i]!.spec;
  const mixed = ks.claims.some((c) => c.spec.point !== c0.point || c.spec.x !== c0.x);
  if (mixed && (v.kind === 'false' || v.kind === 'trap') && v.message)
    out.message = `Quand ${c0.x} ${ARROW} ${c0.point} : ${v.message.charAt(0).toLowerCase()}${v.message.slice(1)}`;
  if (v.kind === 'step' || v.kind === 'ready' || (v.kind === 'useless' && v.lmove)) {
    // ce que la ligne tranche (sans l'écrire dans l'état)
    const before = decisionsOf(ks);
    const after = decisionsAfter(
      ks,
      speaking.length ? speaking.map((s) => s.i) : [best!.i],
      chips,
      opts,
    );
    const decided = after.flatMap((d, i) =>
      before[i] === null && d !== null ? [{ index: i, truth: d }] : [],
    );
    if (decided.length) out.decided = decided;
    const final = after.every((d) => d !== null);
    out.final = final;
    if (v.kind === 'step' && v.rule && decided.length) {
      const parts = decided.map((d) => {
        const c = ks.claims[d.index]!.spec;
        return `« ${c.expr} ${ARROW} ${c.value} » est ${d.truth ? 'vraie' : 'fausse'}.`;
      });
      out.rule = {
        ...v.rule,
        why: `${v.rule.why.replace(' C’est la réponse.', '')} ${parts.join(' ')}`.trim(),
      };
    }
  }
  return out;
}

/** les décisions si la ligne était gardée */
function decisionsAfter(
  ks: KLcState,
  idx: number[],
  chips: Chip[],
  opts?: { verify?: boolean },
): (boolean | null)[] {
  const concluded = ks.claims.map((c, i) => {
    if (c.ks.concluded) return true;
    if (!idx.includes(i)) return false;
    const v = klimJudge(c.ks, chips, opts);
    if (!(v.kind === 'step' || v.kind === 'ready' || v.kind === 'useless') || !v.lmove)
      return false;
    return !!v.final;
  });
  const d = ks.claims.map((c, i) => (concluded[i] ? c.truth : null));
  if (ks.single) {
    const trues = d.filter((v) => v === true).length;
    if (trues === 1) return d.map((v) => v === true);
    const open = d.filter((v) => v === null).length;
    if (!trues && open === 1) return d.map((v) => v === null);
  }
  return d;
}

export function klcCommit(ks: KLcState, v: KLcVerdict): void {
  const text = klimText(v.atoms);
  const exprs = lineExprs(v.atoms);
  const chips = chipsOf(v.atoms);
  // la ligne s'inscrit dans chaque affirmation dont elle parle et où elle est juste
  ks.claims.forEach((c, i) => {
    if (!speaksOf(c, exprs) && i !== v.claim) return;
    const w = i === v.claim ? v : klimJudge(c.ks, chips, { verify: true });
    if ((w.kind === 'step' || w.kind === 'useless' || w.kind === 'ready') && w.lmove)
      klimCommit(c.ks, w);
  });
  ks.decisions = decisionsOf(ks);
  ks.texts.push(text);
  ks.lines++;
}

export function klcDecisions(ks: KLcState): (boolean | null)[] {
  return ks.decisions;
}

export function klcGoalReached(ks: KLcState): boolean {
  return ks.decisions.every((d) => d !== null);
}

/** la ligne est vraie (au point d'une affirmation dont elle parle) */
export function klcHolds(ks: KLcState, text: string): boolean {
  let atoms: string[];
  try {
    atoms = klimLineAtoms(text);
  } catch {
    return false;
  }
  const exprs = lineExprs(atoms);
  return ks.claims.some((c) => speaksOf(c, exprs) && klimHolds(c.ks, text));
}

/** une ligne déjà établie */
export function klcEstablished(ks: KLcState, text: string): boolean {
  let atoms: string[];
  try {
    atoms = klimLineAtoms(text);
  } catch {
    return false;
  }
  if (ks.texts.includes(klimText(atoms))) return true;
  const exprs = lineExprs(atoms);
  return ks.claims.some((c) => speaksOf(c, exprs) && klimEstablished(c.ks, text));
}

/** coup de pouce : la ligne suivante du corrigé, pour la première affirmation pas tranchée */
export function klcNextMove(
  ks: KLcState,
): { line: Line; text: string; atoms: string[]; label: string } | null {
  if (klcGoalReached(ks)) return null;
  for (let i = 0; i < ks.claims.length; i++) {
    if (ks.decisions[i] !== null) continue;
    const c = ks.claims[i]!;
    for (const t of c.ks.path) {
      if (klimEstablished(c.ks, t)) continue;
      const atoms = klimLineAtoms(t);
      const v = klimJudge(c.ks, chipsOf(atoms), { verify: true });
      return { line: klimLine(t), text: klimText(atoms), atoms, label: v.rule?.label ?? '' };
    }
  }
  return null;
}

/** le corrigé rejoué (porte, plafond des HORA) */
export function klcSolvePath(model: ScratchModel): {
  lines: { line: Line; text: string; atoms: string[]; v: KLcVerdict }[];
  reached: boolean;
} {
  const ks = createKLcState(model);
  const lines: { line: Line; text: string; atoms: string[]; v: KLcVerdict }[] = [];
  for (const t of ks.path) {
    const atoms = klimLineAtoms(t);
    const v = klcJudge(ks, chipsOf(atoms), { verify: true });
    if (v.kind !== 'step') break;
    klcCommit(ks, v);
    lines.push({ line: klimLine(t), text: klimText(atoms), atoms, v });
  }
  return { lines, reached: klcGoalReached(ks) };
}

/* ------------------------------ énoncé, palette ------------------------------ */

/** « quand x → +∞ » : le point de chaque affirmation */
export function klcPointText(c: KernelLimitClaim): string {
  return `quand ${c.x} ${ARROW} ${c.point}`;
}

/** les lignes de l'énoncé : chaque limite affirmée, dans l'ordre des choix */
export function limitClaimsGivenTexts(model: ScratchModel): string[] {
  return (model.kernel?.lclaims ?? []).map((c) => `${c.expr} ${ARROW} ${c.value}`);
}

/**
 * Les bouts (D6 : l'énoncé et le corrigé, jamais les pièges ni les choix) : les variables,
 * chaque expression de l'énoncé, les nombres, les opérations, « → » et les limites.
 */
export function klcPaletteParts(model: ScratchModel): {
  names: string[];
  exprs: string[];
  numbers: string[];
  ops: string[];
  limits: string[];
} {
  const spec = model.kernel!;
  const claims = spec.lclaims ?? [];
  const names = new Set<string>();
  const numbers = new Set<string>();
  const ops = new Set<string>();
  const limits = new Set<string>(['+∞', '−∞']);
  claims.forEach((c, i) => {
    const sub = limitModelOf(model, c, i === 0 ? (spec.path ?? []) : []);
    const p = klimPaletteParts(sub);
    p.names.forEach((n) => names.add(n));
    p.numbers.forEach((n) => numbers.add(n));
    p.ops.forEach((o) => ops.add(o));
    p.limits.forEach((l) => limits.add(l));
    // un côté du point (« 0⁺ ») : ses deux côtés comme limites possibles
    const side = /^(−?\d+(?:,\d+)?)[⁺⁻]$/.exec(c.point);
    if (side) {
      limits.add(side[1]! + '⁺');
      limits.add(side[1]! + '⁻');
    }
  });
  const val = (s: string) => Number(s.replace('−', '-').replace(',', '.'));
  const nums = [...numbers].sort((a, b) => val(a) - val(b) || a.localeCompare(b));
  const opList = [...ops].filter((o) => o !== ARROW);
  return {
    names: [...names],
    exprs: claims.map((c) => c.expr),
    numbers: nums,
    ops: [...opList, ARROW],
    limits: [...limits],
  };
}
